import { localDay } from './property-time';
import type { RecordKind } from './types';

/**
 * Property cases: several municipal records that describe one underlying
 * issue (a complaint, the notice it produced, the permit filed to abate it,
 * the order later recorded against the parcel). Records arrive from separate
 * city systems without shared identifiers, so cases are assembled from typed
 * links: explicit references the city publishes (identifiers, complaint↔NOV
 * pages) and inferred relationships (matching prose within a plausible date
 * window). Every link keeps its evidence so the interface can show why two
 * records were grouped and so a wrong merge stays visible instead of silent.
 */

export type CaseLinkRelation =
  | 'references'
  | 'complaint-nov'
  | 'same-work'
  | 'permit-family'
  | 'recorded-order'
  | 'abatement-permit'
  | 'inspection-violation';

export type CaseLinkOrigin = 'explicit' | 'inferred';

export type CaseLinkConfidence = 'high' | 'medium' | 'low';

export type CaseLink = {
  source: string;
  target: string;
  relation: CaseLinkRelation;
  origin: CaseLinkOrigin;
  confidence: CaseLinkConfidence;
  /** Verbatim city text the link rests on, when prose established it. */
  basis: string;
};

export type CaseEventType =
  | 'filed'
  | 'update'
  | 'inspection'
  | 'review'
  | 'appointment'
  | 'deadline'
  | 'hearing'
  | 'recorded';

export type CaseEvent = {
  key: string;
  recordKey: string;
  date: string;
  time: number;
  type: CaseEventType;
  label: string;
  status: string;
  comment: string;
  actor: string;
  sourceUrl?: string;
};

export type CaseThreadEntry = {
  key: string;
  date: string;
  label: string;
  status: string;
  comment: string;
  actor: string;
};

export type CaseAddendaEntry = {
  station: string;
  date: string;
  reviewResult: string;
  holdDescription: string;
  sourceUrl?: string;
};

export type CaseRecordInput = {
  id: string;
  kind: RecordKind;
  status: string;
  date: string;
  /** Owner-facing headline already derived by the interface. */
  title: string;
  description: string;
  sourceUrl?: string;
  archived: boolean;
  openedAt?: string;
  closedAt?: string;
  /** Free text the city publishes about the record: fields, comments. */
  text: string[];
  /** Explicit identifiers the city page links to (complaint↔NOV pages). */
  linkedIds: string[];
  /** Records the existing duplicate-work matcher already relates. */
  relatedPermitIds: string[];
  /** Compliance period in days when a notice states one. */
  complianceDays: number | null;
  thread: CaseThreadEntry[];
  addenda: CaseAddendaEntry[];
};

export type CaseCategory = 'enforcement' | 'permits' | 'recorder' | 'other';

export type PropertyCase = {
  key: string;
  anchorKey: string;
  category: CaseCategory;
  title: string;
  status: 'open' | 'closed';
  memberKeys: string[];
  links: CaseLink[];
  events: CaseEvent[];
  /** Earliest and latest dated activity across all members. */
  startedAt: string;
  lastActivityAt: string;
  lastActivityTime: number;
  /** Any member joined through an inferred link. */
  inferred: boolean;
};

export type CaseGraph = {
  cases: PropertyCase[];
  /** Case key for every record key. */
  caseByRecord: Map<string, string>;
  links: CaseLink[];
  events: CaseEvent[];
};

export type BuildCasesOptions = {
  now: number;
  parseDate: (value: string | null | undefined) => number | null;
  /**
   * Already-validated links from outside the deterministic linker (model
   * proposals whose basis quote and record keys were checked). They never
   * override an explicit link between the same pair and are dropped when
   * either record is missing.
   */
  extraLinks?: CaseLink[];
  /** The property's zone: an entry dated after its today is still to come. UTC when none. */
  timeZone?: string | null;
};

export const recordKey = (record: Pick<CaseRecordInput, 'kind' | 'id'>) =>
  `${record.kind}:${record.id}`;

const PERMIT_KINDS = new Set<RecordKind>(['building', 'electrical', 'plumbing']);
const ENFORCEMENT_KINDS = new Set<RecordKind>(['nov', 'complaint', 'fire-violation']);
const DAY = 86_400_000;

const isPermitKind = (kind: RecordKind) => PERMIT_KINDS.has(kind);

const ANCHOR_PRIORITY: Partial<Record<RecordKind, number>> = {
  nov: 0,
  complaint: 1,
  'fire-violation': 2,
  '311': 3,
  recorder: 4,
  building: 5,
  electrical: 6,
  plumbing: 7,
  'fire-inspection': 8,
  subdivision: 9,
};

/**
 * City identifiers found in prose: permit numbers ("PA201708215412",
 * "202211156582"), complaint numbers ("NOV#202045653", "complaint 202422166")
 * and inspection numbers. Prefixes are dropped so both spellings match, and
 * DBI's dotted permit form ("2013.0503.6062") collapses to the plain number.
 */
export function referencedIds(text: string) {
  const found = new Set<string>();
  const pattern = /(?:\b[A-Z]{1,3}\s?#?\s?|#\s?|\b)(\d{7,14})\b/gi;
  const flat = text.replace(/\b(\d{4})\.(\d{4})\.(\d{4})\b/g, '$1$2$3');
  for (const match of flat.matchAll(pattern)) found.add(match[1]!);
  return found;
}

const ORDER_TITLE =
  /ORDER\s+(?:OF\s+)?ABATE|ABATEMENT|NTC\s+LIEN|NOTICE\s+OF\s+LIEN|SPCL\s+ASSESS|SPECIAL\s+ASSESS|NOTICE\s+OF\s+VIOLATION|NTC\s+VIOL/i;
const ORDER_PROSE =
  /\b(?:OOA|O\.O\.A\.|order\s+of\s+abatement|abatement\s+order|recorded?\s+(?:the\s+)?(?:NOV|notice|order|lien)|to\s+(?:the\s+)?(?:ASR|assessor|recorder)(?:\s+to\s+be\s+recorded)?|special\s+assessment|lien)\b/i;
const FIRE_HEARING = /\bhearing\b/i;
const FIRE_REFERRED = /\breferred to hearing\b/i;
const FIRE_SYSTEM =
  /\b(fire\s+alarm|sprinkler|standpipe|fire\s+pump|kitchen\s+hood|emergency\s+(?:lighting|generator))\b/i;
const OTHER_AGENCY =
  /\b(?:public\s+health|public\s+works|fire\s+dep(?:artmen)?t|planning\s+dep(?:artmen)?t|water\s+(?:department|power|utilities))\b/i;
const ABATEMENT_PROSE =
  /\b(?:comply\s+with|to\s+abate|abate(?:ment)?\s+of|in\s+response\s+to|to\s+(?:address|correct|resolve))\b.*\b(?:NOV|notice\s+of\s+violation|complaint|violation|order)\b|\b(?:NOV|notice\s+of\s+violation|complaint)\b.*\b(?:comply|abate|correct(?:ion)?|resolve)\b/i;
const APPOINTMENT =
  /\b(?:scheduled|appointment|appt\.?|reinspection\s+scheduled|inspection\s+scheduled|hearing\s+scheduled|schedule[d]?\s+for)\b/i;
const HEARING =
  /\b(?:hearing|director'?s\s+hearing|abatement\s+appeals?\s+board|board\s+of\s+appeals)\b/i;
const DEADLINE_PROSE =
  /\b(?:due|deadline|comply\s+by|abate\s+by|correct\s+by|no\s+later\s+than|expire[sd]?\s+(?:on)?|extension\s+(?:granted\s+)?(?:to|until))\b[^.\n]{0,40}?(\d{1,2}\/\d{1,2}\/\d{2,4})/i;
const DATE_IN_TEXT = /\b\d{1,2}\/\d{1,2}\/(?:\d{4}|\d{2})\b/;

const STOP_WORDS = new Set([
  'the',
  'and',
  'for',
  'with',
  'from',
  'this',
  'that',
  'permit',
  'online',
  'building',
  'electrical',
  'plumbing',
  'work',
  'new',
  'existing',
  'install',
  'installation',
  'replace',
  'replacement',
  'repair',
  'per',
  'plans',
  'plan',
  'unit',
  'units',
  'dwelling',
  'buildings',
  'less',
  'street',
  'only',
  'all',
  'one',
  'two',
  'three',
  'floor',
  'level',
  'each',
  // Permit/inspection progress describes process, not a shared scope of work.
  'final',
  'inspection',
  'reinspection',
  'inspected',
  'pending',
  'scheduled',
  'required',
  'recorded',
  'correction',
  'corrected',
  'complete',
  'completed',
  'approval',
  'approved',
  'issued',
  'filed',
  'status',
]);

function significantTokens(text: string) {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .filter((token) => token.length >= 4 && !/^\d+$/.test(token) && !STOP_WORDS.has(token))
      // "bathrooms" and "bathroom" describe the same room.
      .map((token) =>
        token.length > 5 && token.endsWith('s') && !token.endsWith('ss')
          ? token.slice(0, -1)
          : token,
      )
      .filter((token) => !STOP_WORDS.has(token)),
  );
}

/** Unit designations ("Unit #5", "apt 3B", "suite 200") in a scope of work. */
function unitTags(text: string) {
  const tags = new Set<string>();
  const pattern = /\b(?:unit|apt|apartment|suite|ste)\b\.?\s*#?\s*([0-9]{1,4}[a-z]?|[a-z])\b/gi;
  for (const match of text.matchAll(pattern)) tags.add(match[1]!.toLowerCase());
  return tags;
}

/**
 * Block-level records and page furniture are not part of a parcel's cases:
 * Public Works permits cover a street segment and carry a shared CNN in their
 * identifiers, so linking them would sweep the whole block into one case.
 */
const UNLINKED_KINDS = new Set<RecordKind>(['public-works', 'street-view', 'tax-bill', 'assessor']);

function normalizeYear(value: string) {
  const parts = value.split('/');
  if (parts.length === 3 && parts[2]!.length === 2) {
    const year = Number(parts[2]);
    parts[2] = String(year < 70 ? 2000 + year : 1900 + year);
  }
  return parts.join('/');
}

function addLink(links: Map<string, CaseLink>, link: Omit<CaseLink, 'basis'> & { basis?: string }) {
  if (link.source === link.target) return;
  const [a, b] = [link.source, link.target].sort();
  const key = `${a}|${b}`;
  const existing = links.get(key);
  const next = { ...link, basis: link.basis ?? '' };
  if (!existing || (existing.origin === 'inferred' && next.origin === 'explicit'))
    links.set(key, next);
}

function eventType(
  entry: CaseThreadEntry,
  record: CaseRecordInput,
  time: number,
  now: number,
  tomorrow: number,
): CaseEventType {
  const text = `${entry.label} ${entry.status} ${entry.comment}`;
  if (HEARING.test(text)) return 'hearing';
  // Still to come: later than now, or dated after the property's today.
  const ahead = time > now || time >= tomorrow;
  if (APPOINTMENT.test(text) || (ahead && /inspect/i.test(text))) return 'appointment';
  if (/\b(?:review|addend|plan\s+check|hold)\b/i.test(entry.label)) return 'review';
  if (/inspect/i.test(entry.label) || (record.kind === 'building' && entry.actor))
    return 'inspection';
  if (record.kind === 'recorder') return 'recorded';
  return 'update';
}

function recordEvents(record: CaseRecordInput, options: BuildCasesOptions) {
  const key = recordKey(record);
  const events: CaseEvent[] = [];
  const push = (event: Omit<CaseEvent, 'recordKey' | 'time'> & { time?: number }) => {
    const time = event.time ?? options.parseDate(event.date);
    if (time === null) return;
    events.push({
      ...event,
      recordKey: key,
      time,
      sourceUrl: record.sourceUrl,
    });
  };
  const opened = record.openedAt || record.date;
  if (opened)
    push({
      key: `${key}:filed`,
      date: opened,
      type: record.kind === 'recorder' ? 'recorded' : 'filed',
      label:
        record.kind === 'recorder'
          ? 'Recorded'
          : ENFORCEMENT_KINDS.has(record.kind)
            ? 'Opened'
            : 'Filed',
      status: '',
      comment: '',
      actor: '',
    });
  for (const entry of record.thread) {
    const time = options.parseDate(entry.date);
    if (time === null) continue;
    push({
      key: `${key}:${entry.key}`,
      date: entry.date,
      time,
      type: eventType(
        entry,
        record,
        time,
        options.now,
        nextLocalDay(options.now, options.timeZone),
      ),
      label: entry.label,
      status: entry.status,
      comment: entry.comment,
      actor: entry.actor,
    });
    const deadline = DEADLINE_PROSE.exec(entry.comment);
    if (deadline) {
      const date = normalizeYear(deadline[1]!);
      push({
        key: `${key}:${entry.key}:deadline`,
        date,
        type: HEARING.test(entry.comment) ? 'hearing' : 'deadline',
        label: HEARING.test(entry.comment) ? 'Hearing' : 'Deadline',
        status: '',
        comment: entry.comment,
        actor: entry.actor,
      });
    } else if (
      (APPOINTMENT.test(entry.comment) || HEARING.test(entry.comment)) &&
      DATE_IN_TEXT.test(entry.comment)
    ) {
      const date = normalizeYear(DATE_IN_TEXT.exec(entry.comment)![0]);
      const scheduled = options.parseDate(date);
      if (scheduled !== null && scheduled > time)
        push({
          key: `${key}:${entry.key}:scheduled`,
          date,
          type: HEARING.test(entry.comment) ? 'hearing' : 'appointment',
          label: HEARING.test(entry.comment) ? 'Hearing' : 'Scheduled',
          status: '',
          comment: entry.comment,
          actor: entry.actor,
        });
    }
  }
  record.addenda.forEach((item, index) => {
    const date = item.date || DATE_IN_TEXT.exec(item.holdDescription)?.[0] || '';
    push({
      key: `${key}:addenda-${index}`,
      date: normalizeYear(date),
      type: 'review',
      label: item.station,
      status: item.reviewResult,
      comment: item.holdDescription,
      actor: '',
    });
  });
  if (record.kind === 'nov' && record.complianceDays) {
    const issued = options.parseDate(record.date);
    if (issued !== null) {
      const due = issued + record.complianceDays * DAY;
      push({
        key: `${key}:compliance-deadline`,
        date: new Date(due).toISOString().slice(0, 10),
        time: due,
        type: 'deadline',
        label: 'Compliance deadline',
        status: '',
        comment: `${record.complianceDays}-day compliance period stated on the notice`,
        actor: '',
      });
    }
  }
  return events;
}

/** Typed links between records, from published identifiers and matched prose. */
export function deriveCaseLinks(allRecords: CaseRecordInput[], options: BuildCasesOptions) {
  const records = allRecords.filter((record) => !UNLINKED_KINDS.has(record.kind));
  const links = new Map<string, CaseLink>();
  // Prose drops prefixes and suffixes ("E202608182930" → "202608182930",
  // "1752751-1" → "1752751"), so records are also reachable by their digits.
  const byId = new Map<string, CaseRecordInput[]>();
  for (const record of records) {
    const core = record.id.match(/\d{7,14}/)?.[0];
    for (const id of new Set([record.id, core].filter(Boolean) as string[])) {
      const group = byId.get(id) ?? [];
      group.push(record);
      byId.set(id, group);
    }
  }
  const keyed = new Map(records.map((record) => [recordKey(record), record]));

  for (const record of records) {
    const key = recordKey(record);
    for (const id of record.relatedPermitIds)
      for (const other of byId.get(id) ?? [])
        if (isPermitKind(other.kind))
          addLink(links, {
            source: key,
            target: recordKey(other),
            relation: 'same-work',
            origin: 'explicit',
            confidence: 'high',
          });
    for (const id of record.linkedIds)
      for (const other of byId.get(id) ?? []) {
        // SFFD inspection numbers are short and only name inspections.
        if (record.kind === 'fire-violation' && other.kind !== 'fire-inspection') continue;
        addLink(links, {
          source: key,
          target: recordKey(other),
          relation:
            (record.kind === 'complaint' && other.kind === 'nov') ||
            (record.kind === 'nov' && other.kind === 'complaint')
              ? 'complaint-nov'
              : (record.kind === 'fire-violation' && other.kind === 'fire-inspection') ||
                  (record.kind === 'fire-inspection' && other.kind === 'fire-violation')
                ? 'inspection-violation'
                : 'references',
          origin: 'explicit',
          confidence: 'high',
        });
      }
    const prose = [
      record.description,
      ...record.text,
      ...record.thread.map((entry) => entry.comment),
      ...record.addenda.map((entry) => entry.holdDescription),
    ];
    for (const passage of prose) {
      if (!passage) continue;
      for (const id of referencedIds(passage)) {
        for (const other of byId.get(id) ?? []) {
          if (other === record) continue;
          addLink(links, {
            source: key,
            target: recordKey(other),
            relation:
              isPermitKind(record.kind) && ENFORCEMENT_KINDS.has(other.kind)
                ? 'abatement-permit'
                : 'references',
            origin: 'explicit',
            confidence: 'high',
            basis: passage.trim().slice(0, 240),
          });
        }
      }
    }
  }

  // One SFFD inspection writes each finding as its own violation row; the
  // shared inspection number ties the batch together whether or not the
  // inspection itself is in the dataset.
  const violationsByInspection = new Map<string, CaseRecordInput[]>();
  for (const record of records) {
    if (record.kind !== 'fire-violation') continue;
    for (const id of record.linkedIds) {
      const batch = violationsByInspection.get(id) ?? [];
      batch.push(record);
      violationsByInspection.set(id, batch);
    }
  }
  for (const [first, ...rest] of violationsByInspection.values()) {
    if (!first) continue;
    for (const sibling of rest)
      addLink(links, {
        source: recordKey(first),
        target: recordKey(sibling),
        relation: 'inspection-violation',
        origin: 'explicit',
        confidence: 'high',
      });
  }

  const enforcement = records.filter((record) => ['nov', 'complaint'].includes(record.kind));
  // A complaint and its NOV share one thread, so they count as one candidate.
  const explicitRoot = componentRoots([...links.values()]);
  // Recorded orders and liens rarely name the case; the complaint thread says
  // the notice was sent for recording shortly before the recorder shows it.
  for (const document of records) {
    if (document.kind !== 'recorder' || !ORDER_TITLE.test(document.description)) continue;
    // A lien recorded for Public Health or Public Works is not the building
    // notice's order, however close the dates fall.
    if (OTHER_AGENCY.test(document.text.join(' '))) continue;
    const recorded = options.parseDate(document.date);
    if (recorded === null) continue;
    const candidates: Array<{
      record: CaseRecordInput;
      basis: string;
      gap: number;
    }> = [];
    for (const record of enforcement) {
      for (const entry of record.thread) {
        const time = options.parseDate(entry.date);
        if (time === null) continue;
        const gap = recorded - time;
        if (gap < -30 * DAY || gap > 180 * DAY) continue;
        if (!ORDER_PROSE.test(`${entry.label} ${entry.comment}`)) continue;
        candidates.push({ record, basis: entry.comment || entry.label, gap });
      }
    }
    const cases = new Set(candidates.map((item) => explicitRoot(recordKey(item.record))));
    if (cases.size !== 1) continue;
    const best = candidates.sort((a, b) => Math.abs(a.gap) - Math.abs(b.gap))[0]!;
    addLink(links, {
      source: recordKey(document),
      target: recordKey(best.record),
      relation: 'recorded-order',
      origin: 'inferred',
      confidence: Math.abs(best.gap) <= 45 * DAY ? 'medium' : 'low',
      basis: best.basis.trim().slice(0, 240),
    });
  }

  // A permit "to comply with NOV" without a number: only when one open notice
  // could be meant.
  const permits = records.filter((record) => isPermitKind(record.kind));
  for (const permit of permits) {
    const filed = options.parseDate(permit.date);
    const prose = [permit.description, ...permit.text].join(' ');
    if (filed === null || !ABATEMENT_PROSE.test(prose)) continue;
    if ([...referencedIds(prose)].some((id) => byId.has(id))) continue;
    const candidates = enforcement.filter((record) => {
      const opened = options.parseDate(record.openedAt || record.date);
      return (
        opened !== null &&
        filed - opened >= -7 * DAY &&
        filed - opened <= 3 * 365 * DAY &&
        (!record.archived ||
          (record.closedAt && (options.parseDate(record.closedAt) ?? 0) >= filed))
      );
    });
    if (candidates.length !== 1) continue;
    addLink(links, {
      source: recordKey(permit),
      target: recordKey(candidates[0]!),
      relation: 'abatement-permit',
      origin: 'inferred',
      confidence: 'medium',
      basis: (ABATEMENT_PROSE.exec(prose)?.[0] ?? '').trim().slice(0, 240),
    });
  }

  // Permit families (a fire alarm permit, its revision, the witness test):
  // distinctive shared wording within a few years.
  for (let i = 0; i < permits.length; i += 1) {
    const a = permits[i]!;
    const tokensA = significantTokens(a.description);
    if (tokensA.size < 3) continue;
    const timeA = options.parseDate(a.date);
    for (let j = i + 1; j < permits.length; j += 1) {
      const b = permits[j]!;
      const timeB = options.parseDate(b.date);
      if (timeA !== null && timeB !== null && Math.abs(timeA - timeB) > 3 * 365 * DAY) continue;
      const tokensB = significantTokens(b.description);
      if (tokensB.size < 3) continue;
      const shared = [...tokensA].filter((token) => tokensB.has(token));
      const union = new Set([...tokensA, ...tokensB]).size;
      if (shared.length < 3 || shared.length / union < 0.5) continue;
      addLink(links, {
        source: recordKey(a),
        target: recordKey(b),
        relation: 'permit-family',
        origin: 'inferred',
        confidence: shared.length / union >= 0.7 ? 'medium' : 'low',
        basis: shared.join(', '),
      });
    }
  }

  // One remodel, several permits: the trades pull their own permits around the
  // building permit and describe the same rooms or the same unit. Only when a
  // single building permit fits, so two concurrent jobs stay apart.
  const jobRoot = componentRoots([...links.values()]);
  const buildingPermits = permits.filter((permit) => permit.kind === 'building');
  for (const trade of permits) {
    if (trade.kind === 'building') continue;
    const filed = options.parseDate(trade.date);
    if (filed === null) continue;
    const tradeTokens = significantTokens(trade.description);
    const tradeUnits = unitTags(trade.description);
    const candidates: Array<{ permit: CaseRecordInput; basis: string[] }> = [];
    for (const permit of buildingPermits) {
      const started = options.parseDate(permit.date);
      if (started === null) continue;
      if (filed - started < -60 * DAY || filed - started > 365 * DAY) continue;
      const units = unitTags(permit.description);
      const sameUnit = [...units].filter((unit) => tradeUnits.has(unit));
      if (units.size && tradeUnits.size && !sameUnit.length) continue;
      const shared = [...significantTokens(permit.description)].filter((token) =>
        tradeTokens.has(token),
      );
      if (!sameUnit.length && shared.length < 2) continue;
      candidates.push({
        permit,
        basis: [...sameUnit.map((unit) => `unit ${unit}`), ...shared],
      });
    }
    const jobs = new Set(candidates.map((item) => jobRoot(recordKey(item.permit))));
    if (jobs.size !== 1) continue;
    const best = candidates.sort((a, b) => b.basis.length - a.basis.length)[0]!;
    addLink(links, {
      source: recordKey(trade),
      target: recordKey(best.permit),
      relation: 'same-work',
      origin: 'inferred',
      confidence: best.basis.length >= 2 ? 'medium' : 'low',
      basis: best.basis.join(', '),
    });
  }

  // A fire inspection of a named system ("Fire Alarm Inspection") shortly
  // after permits for that system, when only one permit family qualifies.
  const familyRoot = componentRoots([...links.values()]);
  for (const inspection of records) {
    if (inspection.kind !== 'fire-inspection') continue;
    const system = FIRE_SYSTEM.exec(
      `${inspection.title} ${inspection.description}`,
    )?.[1]?.toLowerCase();
    const inspected = options.parseDate(inspection.date);
    if (!system || inspected === null) continue;
    const candidates = permits.filter((permit) => {
      const filed = options.parseDate(permit.date);
      return (
        filed !== null &&
        inspected - filed >= -14 * DAY &&
        inspected - filed <= 180 * DAY &&
        permit.description.toLowerCase().includes(system)
      );
    });
    const families = new Set(candidates.map((permit) => familyRoot(recordKey(permit))));
    if (families.size !== 1) continue;
    const nearest = candidates.sort(
      (a, b) =>
        Math.abs(inspected - (options.parseDate(a.date) ?? 0)) -
        Math.abs(inspected - (options.parseDate(b.date) ?? 0)),
    )[0]!;
    addLink(links, {
      source: recordKey(inspection),
      target: recordKey(nearest),
      relation: 'same-work',
      origin: 'inferred',
      confidence: 'medium',
      basis: inspection.title.trim().slice(0, 240),
    });
  }

  // SFFD's hearing reinspection never names the inspection whose findings were
  // referred to hearing; when exactly one referred batch precedes it, that is
  // the case it re-checks.
  const referralRoot = componentRoots([...links.values()]);
  for (const inspection of records) {
    if (
      inspection.kind !== 'fire-inspection' ||
      !FIRE_HEARING.test(`${inspection.title} ${inspection.description}`)
    )
      continue;
    const inspected = options.parseDate(inspection.date);
    if (inspected === null) continue;
    const referred = records.filter((violation) => {
      if (violation.kind !== 'fire-violation') return false;
      if (!FIRE_REFERRED.test(violation.status)) return false;
      const cited = options.parseDate(violation.date);
      return cited !== null && inspected - cited >= 0 && inspected - cited <= 2 * 365 * DAY;
    });
    const batches = new Set(referred.map((violation) => referralRoot(recordKey(violation))));
    if (batches.size !== 1) continue;
    addLink(links, {
      source: recordKey(inspection),
      target: recordKey(referred[0]!),
      relation: 'inspection-violation',
      origin: 'inferred',
      confidence: 'medium',
      basis: inspection.title.trim().slice(0, 240),
    });
  }

  for (const link of options.extraLinks ?? []) addLink(links, link);

  return [...links.values()].filter((link) => keyed.has(link.source) && keyed.has(link.target));
}

const GENERIC_TITLE =
  /^(?:as observed(?: during| at)?(?: the)?(?: complaint)?(?: inspection)?\.?|complaint inspection|notice of violation|building complaint|violation|complaint|inspector'?s?'? comments?)$/i;

/** First member title that says what the issue is, not just where it came from. */
function caseTitle(ranked: CaseRecordInput[]) {
  const specific = ranked.find(
    (record) => record.title.trim().length >= 12 && !GENERIC_TITLE.test(record.title.trim()),
  );
  const title = (specific ?? ranked[0]!).title.trim();
  return title.length > 140 ? `${title.slice(0, 137).trimEnd()}…` : title;
}

/** Connected components over a set of links; returns a root lookup. */
function componentRoots(links: CaseLink[]) {
  const parent = new Map<string, string>();
  const find = (key: string): string => {
    const current = parent.get(key) ?? key;
    if (current === key) return key;
    const root = find(current);
    parent.set(key, root);
    return root;
  };
  for (const link of links) {
    const [a, b] = [find(link.source), find(link.target)];
    if (a !== b) parent.set(a, b);
  }
  return find;
}

function category(members: CaseRecordInput[]): CaseCategory {
  if (members.some((record) => ENFORCEMENT_KINDS.has(record.kind))) return 'enforcement';
  if (members.some((record) => isPermitKind(record.kind))) return 'permits';
  if (members.some((record) => record.kind === 'recorder')) return 'recorder';
  return 'other';
}

/** Assemble cases from typed links; every record belongs to exactly one case. */
export function buildCases(records: CaseRecordInput[], options: BuildCasesOptions): CaseGraph {
  const links = deriveCaseLinks(records, options);
  const parent = new Map<string, string>();
  const find = (key: string): string => {
    const current = parent.get(key) ?? key;
    if (current === key) return key;
    const root = find(current);
    parent.set(key, root);
    return root;
  };
  const union = (a: string, b: string) => {
    const [ra, rb] = [find(a), find(b)];
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const record of records) parent.set(recordKey(record), recordKey(record));
  for (const link of links) union(link.source, link.target);

  const groups = new Map<string, CaseRecordInput[]>();
  for (const record of records) {
    const root = find(recordKey(record));
    const group = groups.get(root) ?? [];
    group.push(record);
    groups.set(root, group);
  }
  const events = records.flatMap((record) => recordEvents(record, options));
  const eventsByRecord = new Map<string, CaseEvent[]>();
  for (const event of events) {
    const list = eventsByRecord.get(event.recordKey) ?? [];
    list.push(event);
    eventsByRecord.set(event.recordKey, list);
  }
  const caseByRecord = new Map<string, string>();
  const cases: PropertyCase[] = [];
  for (const members of groups.values()) {
    const ranked = [...members].sort(
      (a, b) =>
        (ANCHOR_PRIORITY[a.kind] ?? 99) - (ANCHOR_PRIORITY[b.kind] ?? 99) ||
        (options.parseDate(a.openedAt || a.date) ?? Number.MAX_SAFE_INTEGER) -
          (options.parseDate(b.openedAt || b.date) ?? Number.MAX_SAFE_INTEGER),
    );
    const anchor = ranked[0]!;
    const memberKeys = members.map(recordKey);
    const memberSet = new Set(memberKeys);
    const caseLinks = links.filter(
      (link) => memberSet.has(link.source) && memberSet.has(link.target),
    );
    const caseEvents = memberKeys
      .flatMap((key) => eventsByRecord.get(key) ?? [])
      .sort((a, b) => b.time - a.time);
    const dated = caseEvents.filter((event) => event.time <= options.now);
    const latest = dated[0];
    const earliest = caseEvents.at(-1);
    const key = `case:${recordKey(anchor)}`;
    for (const member of memberKeys) caseByRecord.set(member, key);
    cases.push({
      key,
      anchorKey: recordKey(anchor),
      category: category(members),
      title: caseTitle(ranked),
      status: members.some((record) => !record.archived) ? 'open' : 'closed',
      memberKeys,
      links: caseLinks,
      events: caseEvents,
      startedAt: earliest?.date ?? anchor.date,
      lastActivityAt: latest?.date ?? anchor.date,
      lastActivityTime: latest?.time ?? options.parseDate(anchor.date) ?? 0,
      inferred: caseLinks.some((link) => link.origin === 'inferred'),
    });
  }
  cases.sort(
    (a, b) =>
      Number(b.status === 'open') - Number(a.status === 'open') ||
      b.lastActivityTime - a.lastActivityTime,
  );
  return { cases, caseByRecord, links, events };
}

/** The start of the day after the property's today, as a date-only value parses (UTC midnight). */
function nextLocalDay(now: number, timeZone?: string | null) {
  const local = localDay(new Date(now), timeZone);
  return (local ? Date.parse(`${local}T00:00:00Z`) : now - (now % DAY)) + DAY;
}

/** Appointments, hearings and deadlines from the property's today forward, soonest first. */
export function upcomingEvents(
  graph: Pick<CaseGraph, 'events'>,
  now: number,
  timeZone?: string | null,
) {
  const today = nextLocalDay(now, timeZone) - DAY;
  return graph.events
    .filter(
      (event) => ['appointment', 'deadline', 'hearing'].includes(event.type) && event.time >= today,
    )
    .sort((a, b) => a.time - b.time);
}

/** Dated activity newest first, excluding the synthetic filed/recorded marker. */
export function recentEvents(graph: Pick<CaseGraph, 'events'>, now: number, limit = 5) {
  return graph.events
    .filter((event) => event.time <= now && event.type !== 'filed' && event.type !== 'recorded')
    .sort((a, b) => b.time - a.time)
    .slice(0, limit);
}
